import { Fragment, FC, useEffect, useState } from "react";
import { Box, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { RedoxAllergyType } from "DataTypes/Services/Patient.type";
import Loader from "Components/Loader";
import NoData from "Components/NoData";
import { PatientInitialStateType, UserInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import { SideModalProps } from "DataTypes/Modal";
import RedoxFormatter from "Utils/Redox.Formatter";
import { CommonStyle } from "Components";
import ImgPath from "Constant/Imgs";
import UpdateAllergy from "./UpdateAllergy";
import TabPanel from "Components/TabPanel";
import { label } from "Constant/En";
import DateTime from "Utils/DateTime";
import RedoxApi from "Service/Redox.api";
import CstmBtn from "Components/CstmBtn";
import useEhrSync from "Hooks/useEhrSync";
import EhrLastSync from "Components/EhrLastSync";
import useEhrRefresh from "Hooks/useEhrRefresh";
import { EhrDomains } from "Constant/Ehr";

export interface DataActionType {
  data: RedoxAllergyType | null;
  action: "ADD" | "EDIT" | "VIEW" | "DELETE" | null;
}

const initailState: DataActionType = { data: null, action: null };

const a11yProps: (index: number) => {
  id: string;
  "aria-controls": string;
} = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

const Allergy: FC<SideModalProps> = (props) => {
  const { onClose } = props;
  const { patientInfo, patientOrg, ehr }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient,
  );
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user,
  );
  const crudPermission = myInfo.roleId != 6 && ehr.vendor === null;
  const [allergyList, setAllergyList] = useState<RedoxAllergyType[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dataAction, setDataAction] = useState<DataActionType>(initailState);

  useEffect(() => {
    getAllergyData();
    setDataAction(initailState);
  }, [tabValue]);

  const getAllergyData: () => void = async () => {
    setLoading(true);
    const payload = { allergieActive: tabValue == 0 ? "active" : "history" };
    const res: any = await RedoxApi.AllergySearch({
      id1: patientInfo.id,
      data: payload,
    });
    if (res?.success) {
      setAllergyList(RedoxFormatter.AllergiesList(res?.data?.allergies));
    }
    setLoading(false);
  };

  const ehrSync = useEhrSync(EhrDomains.ALLERGIES);
  useEhrRefresh(EhrDomains.ALLERGIES, getAllergyData);

  const handleCloseLayer2: () => void = () => {
    setDataAction({ data: null, action: null });
  };

  const displayMedsHead: (data: RedoxAllergyType) => React.ReactElement = (
    data,
  ) => {
    return (
      <Box sx={{ ...sxMedBox }}>
        <Typography variant="subtitle1" sx={{ ...sxMedName }}>
          {data.substanceName || label.naText}
        </Typography>
        {crudPermission && (
          <Box
            display="flex"
            justifyContent={"flex-end"}
            sx={{ "&>div": { ml: 1 } }}
            width="30%"
          >
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm }}
              onClick={() => setDataAction({ data: data, action: "EDIT" })}
            >
              <ImgPath.EditOutlined />
            </Box>
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm }}
              onClick={() => setDataAction({ data: data, action: "DELETE" })}
            >
              <ImgPath.DeleteOutline />
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const ActiveUIContent = (
    <Fragment>
      <Box sx={{ ...sxMain }}>
        {loading ? (
          <Loader />
        ) : allergyList.length ? (
          allergyList.map((el) => {
            return (
              <Box key={el.id} sx={{ ...sxContainer }}>
                {displayMedsHead(el)}
              </Box>
            );
          })
        ) : (
          <NoData />
        )}
      </Box>
    </Fragment>
  );

  const HistoricalUIContent = (
    <Fragment>
      <Box sx={{ ...sxMain }}>
        {loading ? (
          <Loader />
        ) : allergyList.length ? (
          allergyList.map((el) => {
            return (
              <Box key={el.id} sx={{ ...sxContainer }}>
                {displayMedsHead(el)}
                <Typography variant="subtitle1">
                  Stop Date: {DateTime.ToLocalDate(el.endDate) || label.naText}
                </Typography>
              </Box>
            );
          })
        ) : (
          <NoData />
        )}
      </Box>
    </Fragment>
  );

  const Layer1 = (
    <Fragment>
      <Box sx={{ ...sxHeader, ...CommonStyle.sxRow }}>
        <Typography fontWeight="bold">Allergies</Typography>
        {/* Controls on one line, with the sync stamp beneath them:
            stacking it under the button alone makes that column taller
            and drops the icons out of line with it. */}
        <Box
          sx={{
            ...CommonStyle.sxFlexDirectionColumn,
            alignItems: "flex-end",
          }}
        >
          <Box sx={{ ...CommonStyle.sxRow }}>
            {ehrSync.canSync && (
              <Tooltip arrow placement="top" title={ehrSync.title}>
                <span>
                  <CstmBtn
                    label={label.syncFromEhr}
                    btnType="primary"
                    loading={ehrSync.syncing}
                    loadingText="Syncing..."
                    onClick={ehrSync.handleSync}
                    sxProps={{ mr: 1, py: 0.4, minWidth: "auto", px: 1.2 }}
                  />
                </span>
              </Tooltip>
            )}
            {crudPermission && (
              <Box
                sx={{ ...CommonStyle.sxRoundIconsMainSm, mr: 1 }}
                onClick={() => setDataAction({ data: null, action: "ADD" })}
              >
                <ImgPath.PlusIcon titleAccess="Add Allergies" />
              </Box>
            )}
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm }}
              onClick={() => {
                handleCloseLayer2();
                onClose();
              }}
            >
              <ImgPath.CloseIcon titleAccess="Close" />
            </Box>
          </Box>
          <EhrLastSync sync={ehrSync} />
        </Box>
      </Box>
      <Box sx={{ ...sxTab }}>
        <Tabs
          value={tabValue}
          onChange={(event, index) => {
            setTabValue(index);
          }}
          aria-label="basic tabs example"
          variant="fullWidth"
          indicatorColor="secondary"
        >
          <Tab label="Active" {...a11yProps(0)} />
          <Tab label="Historical" {...a11yProps(1)} />
        </Tabs>
      </Box>
      <TabPanel value={tabValue} index={0} style={{ ...sxTabPanel }}>
        {ActiveUIContent}
      </TabPanel>
      <TabPanel value={tabValue} index={1} style={{ ...sxTabPanel }}>
        {HistoricalUIContent}
      </TabPanel>
    </Fragment>
  );

  return (
    <Fragment>
      {/* The list column keeps a pixel floor so its rows stop collapsing
          into a horizontal scroll on a narrow screen, and the panel beside
          it takes whatever width is left. */}
      <Box
        sx={{
          ...CommonStyle.sxFlexDirectionColumn,
          width: "30%",
          minWidth: 320,
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        {Layer1}
      </Box>
      <Box width="1%"></Box>
      {crudPermission && dataAction.action && (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <UpdateAllergy
            dataAction={dataAction}
            onClose={handleCloseLayer2}
            refreshList={getAllergyData}
          />
        </Box>
      )}
    </Fragment>
  );
};
export default Allergy;

const sxTab = {
  flexShrink: 0,
  px: 2,
  "&>div": {
    borderBottom: 1,
    borderColor: "custom.main",
  },
};

// The header sizes to its own content rather than to a percentage of the
// modal: with the sync stamp on a second line it needs more than the 10% a
// short viewport gives it, and the overflow used to land on the list below.
const sxHeader = {
  justifyContent: "space-between",
  flexShrink: 0,
  py: 1,
  px: 2,
};

const sxTabPanel = {
  flex: 1,
  minHeight: 0,
};

const sxMain = {
  height: "100%",
  overflow: "auto",
  px: 2,
};

const sxContainer = {
  py: 1,
  borderBottom: "1px solid",
  borderBottomColor: "custom.main",
};

const sxMedBox = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
};

const sxMedName = {
  fontWeight: "bold",
  width: "80%",
  lineBreak: "anywhere",
};
